import { useCallback, useEffect, useRef } from 'react';

/**
 * Create a throttled function handler, it will immediately call the function and then
 * ignore any calls for the specified duration. After the duration has passed, it will
 * allow the next call to go through.
 * If the duration is not specified, the function callback will be returned as is.
 */
export const useThrottledCallback = <T extends unknown[]>(
  callback: (...args: T) => void,
  delay?: number,
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const throttledCallback = useCallback(
    (...args: T) => {
      if (timeoutRef.current !== null) {
        return;
      }
      callback(...args);

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
      }, delay);
    },
    [callback, delay],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return delay === undefined ? callback : throttledCallback;
};
